import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Button from "../ui/Button";
import { Cctv } from "lucide-react";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [menuOpen]);

  // Is Home Page logic
  const isHomePage = [
    "/about",
    "/biospecimens-services",
    "/biospecimens-category",
  ].includes(currentPath);

  const menuItems = [
    { text: "Home", href: "/", disable: false },
    { text: "About Us", href: "/about", disable: false },
    { text: "Biospecimens", href: "/biospecimens-services", disable: false },
    { text: "Biostorage", href: "/biostorage", disable: false },
    { text: "Central Lab Testing", href: "/lab-testing", disable: false },
    {
      text: "Quality & Compliance",
      href: "/quality-compliance",
      disable: false,
    },
  ];

  return (
    <header
      className={`w-full border-b sticky top-0 z-50 transition-all duration-300 ${
        isHomePage
          ? "bg-transparent border-white/10"
          : "bg-white border-gray-100"
      }`}
    >
      <div className="max-w-[1800px] mx-auto px-4 md:px-6 lg:px-10">
        <div className="flex justify-between items-center h-20 lg:h-24">
          {/* Logo Section */}
          <div className="flex-shrink-0">
            <Link to="/">
              <img
                src={isHomePage ? "/images/logo2.png" : "/images/logo.png"}
                alt="Avinya Research Lab"
                className="h-10 md:h-12 lg:h-14 w-auto object-contain"
              />
            </Link>
          </div>

          <nav className="hidden lg:flex items-center gap-4 2xl:gap-8">
            {menuItems.map((item, index) => {
              const isActive = currentPath === item.href;
              return (
                <Link
                  key={index}
                  to={item.disable ? "#" : item.href}
                  onClick={(e) => item.disable && e.preventDefault()}
                  className={`relative flex flex-col items-center group ${
                    item.disable
                      ? "pointer-events-none cursor-not-allowed opacity-50"
                      : ""
                  }`}
                >
                  <span
                    className={`text-[16px] 2xl:text-[22px] font-normal transition-colors duration-200 ${
                      isHomePage
                        ? "text-white opacity-80 group-hover:opacity-100"
                        : isActive
                          ? "text-[#2E4077] font-semibold"
                          : "text-[#4A4A4A] hover:text-[#2E4077]"
                    }`}
                    style={{ fontFamily: "Bricolage Grotesque, sans-serif" }}
                  >
                    {item.text}
                  </span>

                  {isActive && !isHomePage && (
                    <div className="absolute -bottom-2 w-1.5 h-1.5 bg-[#00A9BB] rounded-full"></div>
                  )}

                  {isActive && isHomePage && (
                    <div className="absolute -bottom-2 w-1.5 h-1.5 bg-white rounded-full"></div>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Desktop Button */}
          <div className="hidden lg:block">
            <Button
              className={`${
                isHomePage
                  ? "bg-white text-[#183076] hover:bg-[#183076] hover:text-white"
                  : "bg-[#183076] text-white hover:bg-white hover:text-[#183076] border border-[#183076]"
              } px-6 py-2.5 rounded-full font-bold text-sm opacity-90 transition-all`}
              text="Book Now"
              to="/rfq"
            />
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 z-[60] ${isHomePage ? "text-white" : "text-[#183076]"}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle Menu"
          >
            {/* Same SVG */}
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={
                  menuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"
                }
              />
            </svg>
          </button>
        </div>

        {/* Mobile Sidebar */}
        <div
          className={`fixed inset-0 bg-black/50 transition-opacity lg:hidden ${menuOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
          onClick={() => setMenuOpen(false)}
        />

        <div
          className={`fixed top-0 right-0 w-[80%] sm:w-[350px] h-full bg-white z-[55] shadow-2xl transform transition-transform duration-300 lg:hidden ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col p-8 pt-24 gap-6">
            {menuItems.map((item, index) => {
              const isActive = currentPath === item.href;
              return (
                <Link
                  key={index}
                  to={item.href}
                  onClick={() => setMenuOpen(false)}
                  className={`text-xl font-medium border-b border-gray-50 pb-2 ${
                    isActive ? "text-[#00A9BB]" : "text-[#2E4077]"
                  }`}
                >
                  {item.text}
                </Link>
              );
            })}
            <Button
              className="bg-[#183076] text-white w-full py-4 rounded-xl mt-4 shadow-md"
              text="Book Now"
              to="/rfq"
              onClick={() => setMenuOpen(false)}
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
